import { Button } from '@/components/UI/Common/Admin/Button';
import { Checkbox } from '@/components/UI/Common/Admin/Checkbox';
import { LoadingSpinner } from '@/components/UI/Common/Admin/LoadingSpinner';
import { PageLayout } from '@/components/UI/Layouts/Admin/PageLayout';
import { Pagination } from '@/components/UI/Pagination/Pagination';
import Table from '@/components/UI/Table/Table';
import { ROUTES } from '@/config/routes';
import { useResourceList } from '@/hooks/useResourceList';
import { useDeleteMany } from '@/queries/admin';
import { ChatGroup, ChatTemplate } from '@/types/chats';
import { TableColumn } from '@/types/table';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { Filters } from '../components/Filters';
import { Badge } from '@/components/UI/Common/Admin/Badge';

const ChatsTemplatesPage = () => {
  const {
    data: templates,
    isLoading,
    reset,
    handleFilterChange,
    handleSortChange,
    handlePageChange,
    handleLimitChange,
    filter,
    page,
    sortOrder,
    totalPages,
    totalItems,
    limit,
    sortBy,
  } = useResourceList('chat-templates');
  const { mutateAsync: deleteTemplates } = useDeleteMany('chat-templates');
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
  const navigate = useNavigate();

  const toggleDelete = () => {
    if (idsToDelete.length === templates?.data.length) {
      setIdsToDelete([]);
    } else {
      setIdsToDelete(templates?.data.map((template) => template.id) || []);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTemplates({ ids: idsToDelete });
      toast.success('Шаблоны чатов успешно удалены');
      setIdsToDelete([]);
    } catch (error) {
      console.error('Error deleting chat templates:', error);
      toast.error('Произошла ошибка при удалении шаблонов чатов');
    }
  };

  const renderCheckBox = (_val: unknown, row: ChatTemplate) => (
    <Checkbox
      className="prevent-row-click"
      checked={idsToDelete.includes(row.id)}
      onChange={() => {
        if (idsToDelete.includes(row.id)) {
          setIdsToDelete(idsToDelete.filter((id) => id !== row.id));
        } else {
          setIdsToDelete([...idsToDelete, row.id]);
        }
      }}
    />
  );

  const columns: TableColumn<ChatTemplate>[] = useMemo(
    () => [
      {
        key: '',
        title: (
          <Checkbox
            onClick={toggleDelete}
            onChange={() => {}}
            checked={idsToDelete.length > 0 && idsToDelete.length === templates?.data.length}
          />
        ),
        render: renderCheckBox,
      },
      {
        key: 'id',
        title: 'ID',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'systemName',
        title: 'Системное имя',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'title',
        title: 'Название',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'sort',
        title: 'Порядок',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'chatGroup',
        title: 'Группа чатов',
        render: (val: unknown) => {
          const group = val as ChatGroup;
          return group?.title ? <Badge variant="info">{group.title}</Badge> : '-';
        },
      },
      {
        key: 'createdAt',
        title: 'Создан',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        key: 'updatedAt',
        title: 'Обновлён',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
    ],
    [templates?.data.length, idsToDelete, sortBy, sortOrder],
  );

  if (isLoading || !templates) {
    return <LoadingSpinner className="h-full" text="Загрузка..." />;
  }

  return (
    <PageLayout
      title="Шаблоны чатов"
      actions={
        <div className="flex gap-4">
          {idsToDelete.length > 0 && (
            <Button onClick={handleDelete} size="md" variant="danger">
              Удалить
            </Button>
          )}
          <Link to={ROUTES.ADMIN.CREATE('chat-templates')}>
            <Button size="md" variant="success">
              Создать
            </Button>
          </Link>
        </div>
      }
    >
      <Filters value={filter} onChange={handleFilterChange} onReset={reset} />

      <div className="overflow-x-auto">
        <Table
          data={templates.data}
          columns={columns}
          emptyMessage="Шаблоны чатов не найдены"
          onRowClick={(row) => navigate(ROUTES.ADMIN.EDIT('chat-templates', row.id))}
        />
      </div>

      <Pagination
        className="mt-10"
        onItemsPerPageChange={handleLimitChange}
        currentPage={page}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={limit}
      />
    </PageLayout>
  );
};

export default ChatsTemplatesPage;
